import { useCallback, useEffect, useState } from 'react';
import { SALES_STATUS_VALUES } from '@autodialer/shared';

import { ApiError } from '../../api/client';
import {
  campaignsApi,
  contactsApi,
  importApi,
  type Campaign,
  type CampaignField,
  type CampaignMember,
  type Contact,
  type ContactFilter,
} from '../../api/campaigns';
import {
  Alert,
  Button,
  ConfirmDialog,
  Field,
  StatusChip,
} from '../../components';
import { useI18n, type TranslationKey } from '../../i18n';
import { useSession } from '../../auth/session-context';
import { ContactForm } from './ContactForm';
import { ImportWizard } from './ImportWizard';

const PAGE_SIZE = 25;
/** Mehr Spalten machen die Tabelle unübersichtlich (04_contacts_csv.md). */
const MAX_FIELD_COLUMNS = 4;

/**
 * Datensätze einer Kampagne: Suche, Filter, Tabelle, Bearbeitung,
 * CSV-Import und -Export.
 */
export function ContactsTab({ campaign }: { campaign: Campaign }): JSX.Element {
  const { t, tError } = useI18n();
  const { principal, handleApiError } = useSession();

  const isAdmin = principal?.kind === 'tenant_user' && principal.isAdmin;

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [fields, setFields] = useState<CampaignField[]>([]);
  const [members, setMembers] = useState<CampaignMember[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);

  const [query, setQuery] = useState('');
  const [salesStatus, setSalesStatus] = useState('');
  const [assignedUserId, setAssignedUserId] = useState('');
  const [archived, setArchived] = useState<'active' | 'archived' | 'all'>('active');

  const [editing, setEditing] = useState<Contact | null>(null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Datensatz, dessen Löschung bestätigt werden soll. */
  const [loeschen, setLoeschen] = useState<Contact | null>(null);

  const filter = (): ContactFilter => ({
    query: query || undefined,
    salesStatus: salesStatus || undefined,
    assignedUserId: assignedUserId || undefined,
    archived: isAdmin ? archived : undefined,
  });

  const load = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      const response = await contactsApi.list(campaign.id, {
        ...filter(),
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setContacts(response.contacts);
      setFields(response.fields);
      setTotal(response.total);
    } catch (caught) {
      handleApiError(caught);
      setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign.id, page, query, salesStatus, assignedUserId, archived, handleApiError, tError]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!isAdmin || campaign.campaignMode !== 'assigned') {
      return;
    }
    void campaignsApi
      .listMembers(campaign.id)
      .then((response) => setMembers(response.members))
      .catch(() => undefined);
  }, [campaign.id, campaign.campaignMode, isAdmin]);

  const run = async (action: () => Promise<unknown>, done?: string): Promise<void> => {
    setError(null);
    setMessage(null);
    setBusy(true);
    try {
      await action();
      await load();
      if (done) {
        setMessage(done);
      }
    } catch (caught) {
      handleApiError(caught);
      setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
    } finally {
      setBusy(false);
    }
  };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const sichtbareFelder = fields.slice(0, MAX_FIELD_COLUMNS);

  if (importing) {
    return (
      <ImportWizard
        campaign={campaign}
        onDone={() => {
          setImporting(false);
          void load();
        }}
      />
    );
  }

  return (
    <section>
      <div className="row-actions">
        <button type="button" className="primary" onClick={() => setCreating(true)}>
          {t('contacts.create')}
        </button>
        {isAdmin ? (
          <>
            <button type="button" onClick={() => setImporting(true)}>
              {t('contacts.import')}
            </button>
            <a className="link-button" href={importApi.exportUrl(campaign.id, filter())}>
              {t('contacts.export')}
            </a>
          </>
        ) : null}
      </div>

      {error ? <Alert tone="error">{error}</Alert> : null}
      {message ? <Alert tone="success">{message}</Alert> : null}

      <div className="form form-inline">
        <Field label={t('contacts.search')} hint={t('contacts.searchHint')}>
          <input
            type="search"
            value={query}
            onChange={(event) => {
              setPage(0);
              setQuery(event.target.value);
            }}
          />
        </Field>

        <Field label={t('contacts.filter.status')}>
          <select
            value={salesStatus}
            onChange={(event) => {
              setPage(0);
              setSalesStatus(event.target.value);
            }}
          >
            <option value="">{t('contacts.filter.all')}</option>
            {SALES_STATUS_VALUES.map((status) => (
              <option key={status} value={status}>
                {t(`status.${status}` as TranslationKey)}
              </option>
            ))}
          </select>
        </Field>

        {isAdmin && campaign.campaignMode === 'assigned' ? (
          <Field label={t('contacts.filter.assigned')}>
            <select
              value={assignedUserId}
              onChange={(event) => {
                setPage(0);
                setAssignedUserId(event.target.value);
              }}
            >
              <option value="">{t('contacts.filter.all')}</option>
              {members.map((member) => (
                <option key={member.userId} value={member.userId}>
                  {member.username ?? member.email}
                </option>
              ))}
            </select>
          </Field>
        ) : null}

        {isAdmin ? (
          <Field label={t('contacts.filter.archived')}>
            <select
              value={archived}
              onChange={(event) => {
                setPage(0);
                setArchived(event.target.value as typeof archived);
              }}
            >
              <option value="active">{t('contacts.filter.archived.active')}</option>
              <option value="archived">{t('contacts.filter.archived.archived')}</option>
              <option value="all">{t('contacts.filter.archived.all')}</option>
            </select>
          </Field>
        ) : null}
      </div>

      {creating ? (
        <ContactForm
          campaign={campaign}
          fields={fields}
          members={members}
          contact={null}
          onCancel={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void load();
          }}
        />
      ) : null}

      {editing ? (
        <ContactForm
          campaign={campaign}
          fields={fields}
          members={members}
          contact={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      ) : null}

      <p className="field-hint">{t('contacts.total', { count: total })}</p>

      {contacts.length === 0 ? (
        <p className="lead">{query || salesStatus ? t('contacts.noResults') : t('contacts.empty')}</p>
      ) : (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>{t('contact.phone')}</th>
                <th>{t('contact.firstName')}</th>
                <th>{t('contact.lastName')}</th>
                {sichtbareFelder.map((field) => (
                  <th key={field.id}>{field.fieldName}</th>
                ))}
                <th>{t('contact.status')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact) => (
                <tr key={contact.id}>
                  <td>{contact.phone}</td>
                  <td>{contact.firstName ?? ''}</td>
                  <td>{contact.lastName ?? ''}</td>
                  {sichtbareFelder.map((field) => (
                    <td key={field.id}>{contact.fields[field.id] ?? ''}</td>
                  ))}
                  <td>
                    <StatusChip tone={statusTon(contact.salesStatus)}>
                      {t(`status.${contact.salesStatus}` as TranslationKey)}
                    </StatusChip>
                    {contact.archived ? <StatusChip>{t('contact.archived')}</StatusChip> : null}
                  </td>
                  <td className="zelle-aktionen">
                    <div className="row-actions">
                      <Button variant="ghost" onClick={() => setEditing(contact)}>
                        {t('campaigns.open')}
                      </Button>
                      {isAdmin ? (
                        <>
                          <Button
                            variant="ghost"
                            disabled={busy}
                            onClick={() =>
                              void run(() =>
                                contact.archived
                                  ? contactsApi.restore(campaign.id, contact.id)
                                  : contactsApi.archive(campaign.id, contact.id),
                              )
                            }
                          >
                            {contact.archived ? t('contact.restore') : t('contact.archive')}
                          </Button>
                          {/* Löschen ist endgültig - deshalb eine eigene Rückfrage. */}
                          <Button variant="ghost" disabled={busy} onClick={() => setLoeschen(contact)}>
                            {t('contact.delete')}
                          </Button>
                        </>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 ? (
        <div className="row-actions">
          <button type="button" disabled={page === 0 || busy} onClick={() => setPage((current) => current - 1)}>
            {t('contacts.previous')}
          </button>
          <span className="field-hint">{t('contacts.page', { page: page + 1, pages })}</span>
          <button
            type="button"
            disabled={page + 1 >= pages || busy}
            onClick={() => setPage((current) => current + 1)}
          >
            {t('contacts.next')}
          </button>
        </div>
      ) : null}

      {loeschen ? (
        <ConfirmDialog
          title={t('dialog.contactDelete')}
          message={t('dialog.contactDeleteText')}
          confirmLabel={t('contact.delete')}
          busy={busy}
          onCancel={() => setLoeschen(null)}
          onConfirm={() => {
            const datensatz = loeschen;
            setLoeschen(null);
            void run(() => contactsApi.remove(campaign.id, datensatz.id));
          }}
        />
      ) : null}
    </section>
  );
}

/** Farbe des Statuschips - der Text sagt dasselbe noch einmal. */
function statusTon(status: Contact['salesStatus']): 'erfolg' | 'warnung' | 'gefahr' | 'neutral' {
  if (status === 'successful') {
    return 'erfolg';
  }
  if (status === 'callback') {
    return 'warnung';
  }
  if (status === 'not_interested') {
    return 'gefahr';
  }
  return 'neutral';
}
